import { z } from 'zod';
import { withExceptionHandling, withExceptionHandlingSync } from './exception-handler';
import { ContextStore } from '../stores';

import { EventData, Tracker, Transport } from '../types';
import { CustomEvent, EventSchemas } from '../validation/schemas';
import customEventSchemas from '../custom-events';
import { EventDataValidator } from '../validation';
import { ListenerTrackerContext } from '../listener/types';
import { initListener, toAttributeData } from '../listener';

export const voidTracker: Tracker = (() => {
  const trackFn = async () => {
    console.warn('Analyticstracker has not been initialized.');
    return undefined;
  };
  const unImplemented = () => {
    throw new Error('Not supported: Analyticstracker has not been initialized.');
  };
  const tracker: Partial<Tracker> = {
    identify: trackFn,
    page: trackFn,
    referrer: () => {
      trackFn();
      return undefined;
    },
    listen: unImplemented,
    awaitInitialized: () => Promise.resolve(),
    toAttributeData,
    getSessionId: () => '',
    startSendingEvents: unImplemented,
    stopSendingEvents: unImplemented,
  };

  Object.keys(customEventSchemas.schemas).forEach(customEvent => {
    tracker[customEvent as CustomEvent] = trackFn;
  });

  return tracker as Tracker;
})();

type CreateTrackerProps = {
  transport: Transport;
  validateInput: EventDataValidator;
  throwOnError: boolean;
  enrichEvent: <T>(input: T) => T;
  contextStore: ContextStore;
};

export const createTracker = <TSchemas extends EventSchemas>(
  { transport, throwOnError, validateInput, enrichEvent, contextStore }: CreateTrackerProps,
  context = {} as ListenerTrackerContext,
): Tracker => {
  const track = <TEvent extends CustomEvent>(event: TEvent) => {
    const { subjectName, action } = customEventSchemas.subjectMapping[event];
    return withExceptionHandling(async input => {
      const validated = validateInput(event, input);
      const transformed = enrichEvent(validated);
      await transport.track(subjectName, { ...transformed, action });
      contextStore.resetReferrer();
      return transformed;
    }, throwOnError);
  };

  const tracker = {
    identify: withExceptionHandling(async user => {
      const validatedUser = validateInput('identify', user);
      contextStore.setOrganisationId(validatedUser.organisationId);
      const transformedUser = enrichEvent(validatedUser);
      await transport.identify(transformedUser);
      return transformedUser;
    }, throwOnError),
    page: withExceptionHandling(async page => {
      const validatedPage = validateInput('page', page);
      context.lastPageEventTimeStamp = performance.now();

      const referredBy = contextStore.getReferrer();
      contextStore.setCurrentPage(validatedPage);
      const enrichedPage = { ...validatedPage, referredBy };

      const transformedPage = enrichEvent(enrichedPage);
      await transport.page(transformedPage);
      contextStore.resetReferrer();
      return transformedPage as EventData<'page'>;
    }, throwOnError),
    referrer: withExceptionHandlingSync((referrer: z.infer<TSchemas['referrer']>) => {
      const validatedReferrer = validateInput('referrer', referrer);
      contextStore.setReferrer(validatedReferrer);
      return validatedReferrer;
    }, throwOnError),
    listen: options => initListener(context, contextStore, options),
    toAttributeData,
    awaitInitialized: transport.awaitInitialized,
    getSessionId: () => contextStore.getContext().sessionId,
    startSendingEvents: transport.startSendingEvents,
    stopSendingEvents: transport.stopSendingEvents,
  } as Tracker;

  context.tracker = tracker;

  Object.keys(customEventSchemas.schemas).forEach(customEvent => {
    tracker[customEvent as CustomEvent] = track(customEvent as CustomEvent) as any;
  });

  return tracker;
};
